import React, { forwardRef, useEffect, useState } from "react";
import {
  Box, Text, Input, InputGroup, InputRightElement,
  Icon, HStack, Select, VStack
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MyDiv from "./datePicker.style";
import { format } from "date-fns";

interface DateRangePickerProps {
  label?: string;
  onChange: (dateRange: { start: Date; end: Date } | null) => void;
  maxDate?: Date;
  defaultCurrentWeek?: boolean;
  showAdvancedFilters?: boolean;
  value?: { start: Date; end: Date } | null;

  onFilterChange?: (
    filter: {
      startDate?: string;
      endDate?: string;
      type?: "week" | "month" | "quarter" | "year";
    } | null
  ) => void;
}

const formatLocalDate = (date: Date): string =>
  format(date, "yyyy-MM-dd");

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  onChange,
  maxDate,
  onFilterChange,
  showAdvancedFilters = true,
  defaultCurrentWeek = false,
  value,
}) => {
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  } | null>(value ?? null);

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [isFromOpen, setIsFromOpen] = useState(false);
  const [isToOpen, setIsToOpen] = useState(false);

  useEffect(() => {
    if (value !== undefined) {
      setDateRange(value);
    }
  }, [value]);

  useEffect(() => {
    if (!defaultCurrentWeek || value !== undefined) return;
    const today = new Date();

    // Monday as first day of week
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;

    const start = new Date(today);
    start.setDate(today.getDate() + diff);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    setDateRange({
      start,
      end,
    });

    onChange({
      start,
      end,
    });
  }, [defaultCurrentWeek]);

  // ── Shared custom input factory ──────────────────────────────────────────
  const makeSingleInput = (
    value: string,
    placeholder: string,
    onClear: () => void,
    onToggle: () => void
  ) =>
    forwardRef<HTMLInputElement>((props, ref) => (
      <InputGroup>
        <Input
          {...props}
          ref={ref}
          placeholder={placeholder}
          value={value}
          readOnly
          size="sm"
          bg="white"
          borderColor="gray.300"
          _hover={{ borderColor: "blue.400" }}
          _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
          onClick={onToggle}
        />
        <InputRightElement>
          {value ? (
            <Icon
              boxSize={2}
              color="red"
              cursor="pointer"
              as={CloseIcon}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
            />
          ) : (
            <ChevronDownIcon color="gray.500" />
          )}
        </InputRightElement>
      </InputGroup>
    ));

  // ── Range picker custom input ─────────────────────────────────────────────
  const rangeValue =
    dateRange?.start && dateRange.end
      ? `${format(dateRange.start, "dd/MM/yyyy")} - ${format(dateRange.end, "dd/MM/yyyy")}`
      : "";

  const RangeCustomInput = forwardRef<HTMLInputElement>((props, ref) => (
    <InputGroup>
      <Input
        {...props}
        ref={ref}
        placeholder="Select Date Range"
        value={rangeValue}
        readOnly
        size="sm"
        bg="white"
        borderColor="gray.300"
        _hover={{ borderColor: "blue.400" }}
        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
        onClick={() => setIsRangeOpen((p) => !p)}
      />
      <InputRightElement>
        {rangeValue ? (
          <Icon
            boxSize={2}
            color="red"
            cursor="pointer"
            as={CloseIcon}
            onClick={() => {
              setDateRange(null);
              onChange(null);
            }}
          />
        ) : (
          <ChevronDownIcon color="gray.500" />
        )}
      </InputRightElement>
    </InputGroup>
  ));

  // ── From date input ───────────────────────────────────────────────────────
  const fromValue = fromDate ? format(fromDate, "dd/MM/yyyy") : "";
  const FromCustomInput = makeSingleInput(
    fromValue,
    "From Date",
    () => {
      setFromDate(null);
      // notify parent only when both cleared / changed
      onFilterChange?.(toDate ? { startDate: undefined, endDate: formatLocalDate(toDate) } : null);
    },
    () => setIsFromOpen((p) => !p)
  );

  // ── To date input ─────────────────────────────────────────────────────────
  const toValue = toDate ? format(toDate, "dd/MM/yyyy") : "";
  const ToCustomInput = makeSingleInput(
    toValue,
    "To Date",
    () => {
      setToDate(null);
      onFilterChange?.(fromDate ? { startDate: formatLocalDate(fromDate), endDate: undefined } : null);
    },
    () => setIsToOpen((p) => !p)
  );

  // ── Filter-By select handler ──────────────────────────────────────────────
  const handleFilterBy = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const today = new Date();
    let start: Date | undefined;
    let end: Date | undefined;

    if (value === "week") {
      const first = today.getDate() - today.getDay(); // Sunday
      start = new Date(today.getFullYear(), today.getMonth(), first);
      end = new Date(today.getFullYear(), today.getMonth(), first + 6);
    } else if (value === "month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (value === "quarter") {
      const q = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), q * 3, 1);
      end = new Date(today.getFullYear(), q * 3 + 3, 0);
    } else if (value === "year") {
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31);
    } else {
      onFilterChange?.(null);
      return;
    }

    if (start && end) {
      // Mirror selection into the From / To pickers for visual feedback
      setFromDate(start);
      setToDate(end);
     onFilterChange?.({
  startDate: formatLocalDate(start),
  endDate: formatLocalDate(end),
  type: value as "week" | "month" | "quarter" | "year",
});
    }
  };

  return (
    <Box mb={4}>
      {label && (
        <Text mb={1} fontWeight="medium" color="gray.700">
          {label}
        </Text>
      )}

      <HStack spacing={3} align="flex-end" flexWrap="wrap">
        {/* ── Date Range Picker ── */}
        <Box>
          <Text mb={1} fontSize="xs" color="gray.500">Date Range</Text>
          <MyDiv>
            <DatePicker
              selected={dateRange?.start}
              onChange={(dates: [Date | null, Date | null] | null) => {
                if (dates) {
                  const [start, end] = dates;
                  setDateRange({ start, end });
                  if (start && end) onChange({ start, end });
                  else onChange(null);
                } else {
                  setDateRange(null);
                  onChange(null);
                }
              }}
              startDate={dateRange?.start}
              endDate={dateRange?.end}
              selectsRange
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              customInput={<RangeCustomInput />}
              open={isRangeOpen}
              onClickOutside={() => setIsRangeOpen(false)}
              popperClassName="chakra-datepicker"
              maxDate={maxDate}
            />
          </MyDiv>
        </Box>
{showAdvancedFilters && (
  <>
        {/* ── From Date ── */}
        <Box>
          <Text mb={1} fontSize="xs" color="gray.500">From</Text>
          <MyDiv>
            <DatePicker
              selected={fromDate}
              onChange={(date: Date | null) => {
                setFromDate(date);
                onFilterChange?.({
                  startDate: date ? formatLocalDate(date) : undefined,
                  endDate: toDate ? formatLocalDate(toDate) : undefined,
                });
              }}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              customInput={<FromCustomInput />}
              open={isFromOpen}
              onClickOutside={() => setIsFromOpen(false)}
              maxDate={toDate ?? maxDate}
              popperClassName="chakra-datepicker"
            />
          </MyDiv>
        </Box>

        {/* ── To Date ── */}
        <Box>
          <Text mb={1} fontSize="xs" color="gray.500">To</Text>
          <MyDiv>
            <DatePicker
              selected={toDate}
              onChange={(date: Date | null) => {
                setToDate(date);
                onFilterChange?.({
                  startDate: fromDate ? formatLocalDate(fromDate) : undefined,
                  endDate: date ? formatLocalDate(date) : undefined,
                });
              }}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              customInput={<ToCustomInput />}
              open={isToOpen}
              onClickOutside={() => setIsToOpen(false)}
              minDate={fromDate ?? undefined}
              maxDate={maxDate}
              popperClassName="chakra-datepicker"
            />
          </MyDiv>
        </Box>

        {/* ── Filter By ── */}
        <Box>
          <Text mb={1} fontSize="xs" color="gray.500">Filter By</Text>
          <Select
            width="160px"
            size="sm"
            placeholder="Filter By"
            onChange={handleFilterBy}
            bg="white"
            borderColor="gray.300"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">Quarterly</option>
            <option value="year">Yearly</option>
          </Select>
        </Box>
        </>
        )}
      </HStack>
    </Box>
  );
};

export default DateRangePicker;